// Parses raw pricing log text into structured data for visualization.
// Handles missing sections, multi-item logs, partial logs, and batch continuations.

export function parsePricingLog(log) {
  if (!log) return null

  const result = {
    summary: extractSummary(log),
    modelValidation: extractModelValidation(log),
    items: extractItems(log),
    confidence: extractConfidence(log),
    datasetConfidence: extractDatasetConfidence(log),
    conditionSegmentation: extractConditionSegmentation(log),
    segmentClassification: extractSegmentClassification(log),
    reconciliation: extractReconciliation(log),
    priceBucketing: extractPriceBucketing(log),
    marketData: extractMarketData(log),
    imageAnalysis: extractImageAnalysis(log),
  }

  result.summary.warnings = deriveWarnings(result)
  return result
}

// Extract text between a --- SECTION --- header and the next --- header (or end of string).
// Does NOT use /m flag — $ means end of string, not end of line.
function getSection(log, sectionName) {
  const re = new RegExp('--- ' + sectionName + ' ---\\n([\\s\\S]*?)(?=\\n---[\\s]|$)')
  return re.exec(log)?.[1]?.trim() || null
}

function extractSummary(log) {
  const status = log.match(/^Status:\s*(\S+)/m)?.[1] || null
  const unitMode = log.match(/^Unit Mode:\s*(\S+)/m)?.[1] || null
  const sellPrice = parseFloat(log.match(/^Sell Price:\s*£([\d.]+)/m)?.[1]) || 0
  const maxBuy = parseFloat(log.match(/^Max Buy:\s*£([\d.]+)/m)?.[1]) || 0
  const estWeight = log.match(/^Est Weight:\s*(\S+)/m)?.[1] || null
  const shipping = parseFloat(log.match(/^Shipping:\s*£([\d.]+)/m)?.[1]) || 0

  const itemLines = log.match(/^Items:\n([\s\S]*?)(?=^Status:|^Unit Mode:|\n\n)/m)
  let itemCount = 0
  let pricedCount = 0
  if (itemLines?.[1]) {
    const lines = itemLines[1].split('\n').filter(l => /^\d+\./.test(l.trim()))
    itemCount = lines.length
    pricedCount = lines.filter(l => !l.includes('(PENDING)')).length
  }

  const fc = parseFloat(log.match(/^FC:\s*([\d.]+)/m)?.[1]) || null

  return {
    status, unitMode, sellPrice, maxBuy, estWeight, shipping,
    itemCount, pricedCount,
    confidence: fc,
    confidenceLevel: fc === null ? null : fc >= 0.6 ? 'HIGH' : fc >= 0.4 ? 'MEDIUM' : 'LOW',
    warnings: [],
  }
}

function extractModelValidation(log) {
  // Check for warning format first
  const warningMatch = log.match(/⚠ MODEL VALIDATION WARNING\nType:\s*(.+)\nDetail:\s*(.+)\nSeverity:\s*(.+)\nAction:\s*(.+?)(?:\nManual.*)?(?:\n|$)/m)
  if (warningMatch) {
    const timestamp = log.match(/\[(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2})\]/)?.[1] || null
    return {
      status: 'WARNING',
      type: warningMatch[1].trim(),
      detail: warningMatch[2].trim(),
      severity: warningMatch[3].trim(),
      action: warningMatch[4].trim(),
      timestamp,
    }
  }

  // Extract the MODEL VALIDATION section content for confirmed/passed cases
  const section = getSection(log, 'MODEL VALIDATION')
  const gateSection = getSection(log, 'MODEL VALIDATION GATE')

  // Determine status from gate result or section content
  const gateMatch = (gateSection || log).match(/Gate result:\s*(\w+)/)
  let status = null
  if (gateMatch) {
    status = gateMatch[1].startsWith('PASS') ? 'PASS' : gateMatch[1]
  } else if (section?.includes('Status: CONFIRMED')) {
    status = 'PASS'
  }
  if (!status) return null

  // Extract useful details from the MODEL VALIDATION section
  const visibleText = section?.match(/Visible Text:\s*(.+)/)?.[1]?.trim()
  const category = section?.match(/Category:\s*(.+)/)?.[1]?.trim()
  const detail = visibleText ? `Visible: ${visibleText}${category ? ` · Category: ${category}` : ''}` : null

  return { status, type: null, detail, severity: null, action: null }
}

function extractItems(log) {
  const items = []
  const itemRegex = /^(\d+)\.\s+(.+?)\s+-\s+(.+?)\s+-\s+.*?Qty:(\d+).*?SP:£([\d.]+).*?BP:£([\d.]+)(?:.*?Sc:(\d+))?(?:\s+\((\w+)\))?/gm
  let match
  while ((match = itemRegex.exec(log)) !== null) {
    items.push({
      index: parseInt(match[1]),
      name: match[2].trim(),
      category: match[3].trim(),
      qty: parseInt(match[4]),
      sp: parseFloat(match[5]),
      bp: parseFloat(match[6]),
      score: match[7] ? parseInt(match[7]) : null,
      status: match[8] || (parseFloat(match[5]) > 0 ? 'priced' : 'pending'),
    })
  }
  return items
}

function extractConfidence(log) {
  const icMatch = log.match(/^IC:\s*([\d.]+)\s*\[(.+?)\]/m)
  const mcMatch = log.match(/^MC:\s*([\d.]+)\s*\[(.+?)\]/m)
  const fcMatch = log.match(/^FC:\s*([\d.]+)\s*→\s*(.+?)(?:\s*\(.*\))?$/m)

  const parseComponents = (str) => {
    const components = {}
    const re = /(\w+)=([\d.]+)×(\d+)%/g
    let m
    while ((m = re.exec(str)) !== null) {
      components[m[1]] = { value: parseFloat(m[2]), weight: parseInt(m[3]) }
    }
    return components
  }

  const adjustments = []
  const adjMatch = log.match(/Image-only SKUs present: IC ([+-][\d.]+)/m)
  if (adjMatch) adjustments.push(`Image-only SKUs: IC ${adjMatch[1]}`)

  return {
    ic: icMatch ? { total: parseFloat(icMatch[1]), components: parseComponents(icMatch[2]) } : null,
    mc: mcMatch ? { total: parseFloat(mcMatch[1]), components: parseComponents(mcMatch[2]) } : null,
    fc: fcMatch ? { total: parseFloat(fcMatch[1]), level: fcMatch[2].trim() } : null,
    adjustments,
  }
}

function extractDatasetConfidence(log) {
  const section = getSection(log, 'DATASET CONFIDENCE SCORE')
  if (!section) return null
  const scoreMatch = section.match(/Score:\s*([\d.]+)\s*\((.+?)\)/)
  return {
    score: scoreMatch ? parseFloat(scoreMatch[1]) : null,
    label: scoreMatch?.[2] || 'N/A',
    detail: section,
  }
}

function extractConditionSegmentation(log) {
  const section = getSection(log, 'CONDITION SEGMENTATION')
  if (!section) return null
  return {
    totalFiltered: parseInt(section.match(/Total filtered:\s*(\d+)/)?.[1]) || 0,
    sparesClassified: parseInt(section.match(/Spares classified:\s*(\d+)/)?.[1]) || 0,
    workingClassified: parseInt(section.match(/Working classified:\s*(\d+)/)?.[1]) || 0,
    contaminationRatio: section.match(/Contamination ratio:\s*(\S+)/)?.[1] || 'N/A',
    level: section.match(/Contamination level:\s*(\S+)/)?.[1] || 'N/A',
  }
}

function extractSegmentClassification(log) {
  const section = getSection(log, 'SEGMENT CLASSIFICATION')
  if (!section) return null
  return {
    method: section.match(/Method:\s*(.+)/)?.[1]?.trim() || 'N/A',
    segments: section.match(/Segments:\s*(.+)/)?.[1]?.trim() || 'N/A',
    assembledCount: parseInt(section.match(/Assembled SKUs:\s*(\d+)/)?.[1]) || 0,
  }
}

function extractReconciliation(log) {
  const section = getSection(log, 'RECONCILIATION')
  if (!section) return null
  return {
    textSKUs: parseInt(section.match(/Text SKUs:\s*(\d+)/)?.[1]) || 0,
    total: parseInt(section.match(/Reconciled:\s*(\d+)/)?.[1]) || 0,
    matched: parseInt(section.match(/Matched:\s*(\d+)/)?.[1]) || 0,
    missingVisual: parseInt(section.match(/Missing visual:\s*(\d+)/)?.[1]) || 0,
    imageOnly: parseInt(section.match(/Image-only:\s*(\d+)/)?.[1]) || 0,
    mismatch: parseInt(section.match(/Mismatch:\s*(\d+)/)?.[1]) || 0,
    riskTier: parseInt(section.match(/Risk Tier:\s*(\d+)/)?.[1]) || 0,
    flaggedCount: parseInt(section.match(/Flagged for review:\s*(\d+)/)?.[1]) || 0,
  }
}

function extractPriceBucketing(log) {
  const items = []
  const bucketSection = log.match(/--- PRICE BUCKETING ANALYSIS ---\n([\s\S]*?)(?=\n--- (?:CONDITION ADJUSTMENT|EBAY UK SOLD|LLM CLASSIFICATION) ---|Cleaned Prices Used:|$)/)?.[1]
  if (!bucketSection) return items

  const blocks = bucketSection.split(/^\[/m).filter(Boolean)
  for (const block of blocks) {
    const nameMatch = block.match(/^(.+?)\]/)
    if (!nameMatch) continue
    const itemName = nameMatch[1].trim()
    const text = block

    const msrMatch = text.match(/MSR:\s*([\d.]+)\s*\((\d+)\s*sold\s*\/\s*(\d+)\s*active\)/)
    const demandMatch = text.match(/Demand:\s*(\S+)/)
    const supplyMatch = text.match(/Supply:\s*(\S+)/)
    const marketStateMatch = text.match(/Market State:\s*(\S+)/)

    const soldStats = {}
    const soldStatsMatch = text.match(/Sold stats:\s*P35=£([\d.]+)\s*P40=£([\d.]+)\s*P45=£([\d.]+)\s*P50=£([\d.]+)/)
    if (soldStatsMatch) {
      soldStats.p35 = parseFloat(soldStatsMatch[1])
      soldStats.p40 = parseFloat(soldStatsMatch[2])
      soldStats.p45 = parseFloat(soldStatsMatch[3])
      soldStats.p50 = parseFloat(soldStatsMatch[4])
    }

    const lowestActive = parseFloat(text.match(/Lowest active:\s*£([\d.]+)/)?.[1]) || null
    const stateLogicLines = text.match(/^(?:STATE [A-Z]|ACTIVE_ONLY|NO_SOLD_DATA):.+$/gm) || []
    const baselineSP = parseFloat(text.match(/Baseline SP \(Excellent\):\s*£([\d.]+)/)?.[1]) || null

    const condSection = text.match(/--- CONDITION ADJUSTMENT ---\n([\s\S]*?)(?=\n---|$)/)?.[1]
    let condition = null, multiplier = null, adjustedSP = null, targetSP = null
    if (condSection) {
      condition = condSection.match(/Intake condition:\s*(.+)/)?.[1]?.trim()
      multiplier = parseFloat(condSection.match(/Multiplier applied:\s*([\d.]+)/)?.[1])
      adjustedSP = parseFloat(condSection.match(/Adjusted SP:\s*£([\d.]+)/)?.[1])
      targetSP = parseFloat(condSection.match(/Target SP:\s*£([\d.]+)/)?.[1])
    }

    const buyLine = text.match(/Buy:\s*(.+)/)?.[1] || ''
    const buyPrice = parseFloat(buyLine.match(/£([\d.]+)(?:\s*$)/)?.[1]) || null
    const oversupplyApplied = text.includes('Oversupply rule:')
    const oversupplyLine = text.match(/Oversupply rule:\s*(.+)/)?.[1] || null

    const caspMatch = text.match(/CASP:\s*£([\d.]+)\s*\(confidence\s*([\d.]+)/)
    const msrHaircut = text.match(/MSR haircut:\s*(.+)/)?.[1] || null
    const volatility = text.match(/Volatility:\s*(.+)/)?.[1] || null

    const iqrSold = text.match(/IQR: removed (\d+) outliers from sold/)?.[1]
    const iqrActive = text.match(/Active IQR: removed (\d+) outliers/)?.[1]
    const structuralActive = text.match(/Active structural: removed (\d+)/)?.[1]
    const cleanedSoldMatch = text.match(/Cleaned sold \((\d+)\)/)
    const cleanedActiveMatch = text.match(/Active \((\d+)(?:,\s*cleaned\s*(\d+))?\)/)

    items.push({
      itemName,
      marketState: marketStateMatch?.[1] || null,
      msr: msrMatch ? parseFloat(msrMatch[1]) : null,
      msrSold: msrMatch ? parseInt(msrMatch[2]) : null,
      msrActive: msrMatch ? parseInt(msrMatch[3]) : null,
      demand: demandMatch?.[1] || null,
      supply: supplyMatch?.[1] || null,
      soldStats, lowestActive,
      stateLogic: stateLogicLines.join('\n'),
      baselineSP, condition, multiplier, adjustedSP, targetSP,
      casp: caspMatch ? parseFloat(caspMatch[1]) : null,
      caspConfidence: caspMatch ? parseFloat(caspMatch[2]) : null,
      buyCalc: buyLine, buyPrice, oversupplyApplied, oversupplyLine,
      msrHaircut, volatility,
      cleaning: {
        iqrSoldRemoved: iqrSold ? parseInt(iqrSold) : 0,
        iqrActiveRemoved: iqrActive ? parseInt(iqrActive) : 0,
        structuralRemoved: structuralActive ? parseInt(structuralActive) : 0,
        cleanedSoldCount: cleanedSoldMatch ? parseInt(cleanedSoldMatch[1]) : null,
        totalActive: cleanedActiveMatch ? parseInt(cleanedActiveMatch[1]) : null,
        cleanedActiveCount: cleanedActiveMatch?.[2] ? parseInt(cleanedActiveMatch[2]) : null,
      },
    })
  }

  // Extract condition adjustments that appear AFTER the bucketing section
  const condBlocks = log.match(/--- CONDITION ADJUSTMENT ---\n([\s\S]*?)(?=\n--- CONDITION ADJUSTMENT ---|\n--- EBAY|Cleaned Prices Used:|\n--- LLM|$)/g) || []
  condBlocks.forEach((block, i) => {
    if (i < items.length && !items[i].condition) {
      const condition = block.match(/Intake condition:\s*(.+)/)?.[1]?.trim()
      const multiplier = parseFloat(block.match(/Multiplier applied:\s*([\d.]+)/)?.[1])
      const adjustedSP = parseFloat(block.match(/Adjusted SP:\s*£([\d.]+)/)?.[1])
      const targetSP = parseFloat(block.match(/Target SP:\s*£([\d.]+)/)?.[1])
      const casp = parseFloat(block.match(/CASP:\s*£([\d.]+)/)?.[1])
      const caspConf = parseFloat(block.match(/confidence\s*([\d.]+)/)?.[1])
      const buyLine = block.match(/Buy:\s*(.+)/)?.[1] || ''
      const buyPrice = parseFloat(buyLine.match(/→\s*£([\d.]+)\s*$/)?.[1]) || parseFloat(buyLine.match(/£([\d.]+)\s*$/)?.[1]) || null
      const oversupply = block.includes('Oversupply rule:')
      const oversupplyLine = block.match(/Oversupply rule:\s*(.+)/)?.[1] || null

      items[i].condition = condition
      items[i].multiplier = multiplier || items[i].multiplier
      items[i].adjustedSP = adjustedSP || items[i].adjustedSP
      items[i].targetSP = targetSP || items[i].targetSP
      if (casp) items[i].casp = casp
      if (caspConf) items[i].caspConfidence = caspConf
      if (buyLine) items[i].buyCalc = buyLine
      if (buyPrice) items[i].buyPrice = buyPrice
      if (oversupply) items[i].oversupplyApplied = true
      if (oversupplyLine) items[i].oversupplyLine = oversupplyLine
    }
  })

  return items
}

function extractMarketData(log) {
  const perItem = []

  // Step 1: Per-item eBay data from Model filter blocks
  // Model filter blocks end at the NEXT Model filter OR at Google Shopping section
  const googleSectionStart = log.indexOf('--- GOOGLE SHOPPING')
  const modelFilters = [...log.matchAll(/Model filter: raw="(.+?)"/g)]
  for (let i = 0; i < modelFilters.length; i++) {
    const itemName = modelFilters[i][1]
    if (perItem.find(p => p.itemName === itemName)) continue

    const startPos = modelFilters[i].index
    const nextFilter = i + 1 < modelFilters.length ? modelFilters[i + 1].index : log.length
    // Don't let block extend into Google/Reverb/Amazon sections
    const endPos = (googleSectionStart > startPos && googleSectionStart < nextFilter)
      ? googleSectionStart : nextFilter
    const block = log.slice(startPos, endPos)

    const ebaySold = block.match(/eBay Sold: Found (\d+) results, (\d+) relevant/)
    const ebayActive = block.match(/eBay Active: Found (\d+) results, (\d+) relevant/)

    perItem.push({
      itemName,
      ebay: (ebaySold || ebayActive) ? {
        rawSold: ebaySold ? parseInt(ebaySold[1]) : 0,
        relevantSold: ebaySold ? parseInt(ebaySold[2]) : 0,
        rawActive: ebayActive ? parseInt(ebayActive[1]) : 0,
        relevantActive: ebayActive ? parseInt(ebayActive[2]) : 0,
      } : null,
      google: null,
    })
  }

  // Step 2: Per-item Google data from "[ItemName] Found N Google Shopping results" lines
  const googleMatches = [...log.matchAll(/\[(.+?)\]\s*Found (\d+) Google Shopping results/g)]
  for (let i = 0; i < googleMatches.length; i++) {
    const googleItemName = googleMatches[i][1]
    const rawTotal = parseInt(googleMatches[i][2])
    const startPos = googleMatches[i].index
    const endPos = i + 1 < googleMatches.length ? googleMatches[i + 1].index : log.length
    const block = log.slice(startPos, Math.min(startPos + 1000, endPos))
    const rel = block.match(/Relevant:\s*(\d+),\s*Filtered:\s*(\d+)/)

    // Match to perItem: try contains (longer name first to avoid false matches)
    const sorted = [...perItem].sort((a, b) => b.itemName.length - a.itemName.length)
    const existing = sorted.find(p =>
      googleItemName.toLowerCase().includes(p.itemName.toLowerCase())
    )
    if (existing && !existing.google) {
      existing.google = {
        rawTotal,
        relevant: rel ? parseInt(rel[1]) : 0,
        filtered: rel ? parseInt(rel[2]) : 0,
      }
    }
  }

  // Aggregate sources from all items
  const sources = []
  const ebayTotals = { rawSold: 0, relevantSold: 0, rawActive: 0, relevantActive: 0 }
  const googleTotals = { rawTotal: 0, relevant: 0, filtered: 0 }
  let hasEbay = false, hasGoogle = false

  for (const item of perItem) {
    if (item.ebay) {
      hasEbay = true
      ebayTotals.rawSold += item.ebay.rawSold
      ebayTotals.relevantSold += item.ebay.relevantSold
      ebayTotals.rawActive += item.ebay.rawActive
      ebayTotals.relevantActive += item.ebay.relevantActive
    }
    if (item.google) {
      hasGoogle = true
      googleTotals.rawTotal += item.google.rawTotal
      googleTotals.relevant += item.google.relevant
      googleTotals.filtered += item.google.filtered
    }
  }

  if (hasEbay) sources.push({ name: 'eBay', ...ebayTotals })
  if (hasGoogle) sources.push({ name: 'Google', ...googleTotals })

  // Reverb (single section, not per-item in Debug blocks)
  const reverbSold = log.match(/--- REVERB UK.*---\n(?:[\s\S]*?)Sold:\s*(.+)/)?.[1]
  const reverbActive = log.match(/--- REVERB UK.*---\n(?:[\s\S]*?)Active:\s*(.+)/)?.[1]
  if (reverbSold || reverbActive) {
    sources.push({
      name: 'Reverb',
      soldCount: (reverbSold?.match(/£[\d.]+/g) || []).length,
      activeCount: (reverbActive?.match(/£[\d.]+/g) || []).length,
    })
  }

  // Amazon
  const amazonMatch = log.match(/Amazon:\s*(.+)/m)
  if (amazonMatch) sources.push({ name: 'Amazon', detail: amazonMatch[1].trim() })

  // LLM classification — aggregate across all blocks
  const llmBlocks = [...log.matchAll(/--- LLM CLASSIFICATION ---\nClassified:\s*(\d+)\s*of\s*\d+.*?Included:\s*(\d+),\s*Excluded:\s*(\d+)/gm)]
  const reasons = {}
  let llmTotal = 0, llmIncluded = 0, llmExcluded = 0
  for (const m of llmBlocks) {
    llmTotal += parseInt(m[1])
    llmIncluded += parseInt(m[2])
    llmExcluded += parseInt(m[3])
  }
  if (llmTotal > 0) {
    const excluded = log.match(/\[EXCLUDED\].*?\((\w+)\)/gm) || []
    for (const line of excluded) {
      const reason = line.match(/\((\w+)\)$/)?.[1]
      if (reason) reasons[reason] = (reasons[reason] || 0) + 1
    }
  }

  return {
    sources,
    perItem,
    llmClassification: llmTotal > 0 ? { total: llmTotal, included: llmIncluded, excluded: llmExcluded, reasons } : null,
  }
}

function extractImageAnalysis(log) {
  const section = getSection(log, 'IMAGE DEBUG')
  if (!section) return null

  const imageCount = parseInt(section.match(/(?:Found|Total images loaded:)\s*(\d+)/)?.[1]) || 0
  const ocrMatch = section.match(/\[AZURE-OCR\]\s*(\d+)\/\d+\s*images processed,\s*(\d+)\s*unique lines,\s*avg confidence\s*([\d.]+)/)

  return {
    imageCount,
    ocrProcessed: ocrMatch ? parseInt(ocrMatch[1]) : 0,
    ocrUniqueLines: ocrMatch ? parseInt(ocrMatch[2]) : 0,
    ocrAvgConfidence: ocrMatch ? parseFloat(ocrMatch[3]) : null,
    serialCandidates: section.match(/Serial candidates:\s*(.+)/)?.[1]?.trim() || '',
    modelCandidates: section.match(/Model candidates:\s*(.+)/)?.[1]?.trim() || '',
  }
}

function deriveWarnings(result) {
  const warnings = []
  if (result.modelValidation?.type) warnings.push(result.modelValidation.type.toLowerCase().replace(/_/g, ' '))
  if (result.confidence?.fc?.level?.includes('LOW')) warnings.push('low confidence')
  if (result.priceBucketing?.some(b => b.oversupplyApplied)) warnings.push('oversupply')
  if (result.reconciliation?.flaggedCount > 0) warnings.push(`${result.reconciliation.flaggedCount} flagged`)
  if (result.summary?.status === 'PARTIAL') warnings.push('partial pricing')
  return warnings
}
